import {
  Component,
  computed,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import { AccordionGroupComponent } from '../../../../../common/accordion-group/accordion-group.component';
import { AccordionItemComponent } from '../../../../../common/accordion-item/accordion-item.component';
import { AlertComponent } from '../../../../../common/alert/alert.component';
import { CommonModule } from '@angular/common';
import { SpinnerComponent } from '../../../../../common/spinner/spinner.component';
import { TabPanelComponent } from '../../../../../common/tab-view/tab-panel.component';
import { TabViewComponent } from '../../../../../common/tab-view/tab-view.component';
import { ApplicantForm } from './forms/applicant.form';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ApplicantAppEntityApplicantWrite,
  ApplicationAppEntityApplicationRead,
  EntityTypeAppEntityDictionaryEntityType,
  PatchApplicantApplicantItemRequestParams,
  ServiceAppEntityServiceRead
} from '../../../../../openapi';
import {
  selectApplication,
  selectEntityTypes,
  selectLoading,
  selectPending
} from '../../../../../store/selectors/application.selectors';
import { Subscription } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import {
  createApplication,
  loadEntityTypes,
  setApplication,
  updateApplicants
} from '../../../../../store/actions/application.actions';
import { NaturalPersonFormComponent } from './components/natural-person-form/natural-person-form.component';
import { InstitutionFormComponent } from './components/institution-form/institution-form.component';
import { CompanyFormComponent } from './components/company-form/company-form.component';
import { EntityLabelPipe } from '../../../../../pipes/applicant-label.pipe';
import { ApplicantItemPreviewComponent } from './components/applicant-item-preview/applicant-item-preview.component';
import { selectServiceByUrl } from '../../../../../store/selectors/app.selectors';
import { StagesComponent } from '../../../../../common/stages/stages.component';
import { ProxyPersonForm } from './forms/proxy-person.form';
import { ProxyFormComponent } from './components/proxy-person-form/proxy-form.component';
import { ReactiveFormsModule } from '@angular/forms';
import { ValidationErrorsComponent } from '../../../../../common/validation-errors/validation-errors.component';
import { OtherEntityFormComponent } from './components/other-entity-form/other-entity-form.component';

export interface ApplicantDataComponentConfig {
  stages: string[];
}

@Component({
  selector: 'app-applicant-data-section',
  standalone: true,
  imports: [
    CommonModule,
    AlertComponent,
    AccordionGroupComponent,
    AccordionItemComponent,
    TabPanelComponent,
    TabViewComponent,
    SpinnerComponent,
    NaturalPersonFormComponent,
    InstitutionFormComponent,
    CompanyFormComponent,
    EntityLabelPipe,
    ApplicantItemPreviewComponent,
    StagesComponent,
    ProxyFormComponent,
    ReactiveFormsModule,
    ValidationErrorsComponent,
    OtherEntityFormComponent
  ],
  templateUrl: './applicant-data-section.component.html',
  providers: [
    ApplicantForm,
    ProxyPersonForm
  ]
})
export class ApplicantDataSectionComponent implements OnDestroy, OnInit {

  public serviceType: Signal<ServiceAppEntityServiceRead | null> = this.store$.selectSignal(selectServiceByUrl(this.route.parent?.snapshot.data['contentUrl'] || ''));

  public stages: string[] = this.route.snapshot.data['stages'];

  public applicationId: string | undefined = this.route.snapshot.params['appId'];

  public showItemForm = false;

  public applicants: WritableSignal<PatchApplicantApplicantItemRequestParams[]> = signal([]);

  public entityTypes: Signal<EntityTypeAppEntityDictionaryEntityType[]> = this.store$.selectSignal(selectEntityTypes);

  public application: Signal<ApplicationAppEntityApplicationRead | null> = this.store$.selectSignal(selectApplication);

  private applicationApplicants: Signal<PatchApplicantApplicantItemRequestParams[]> = computed(() => {
    const app = this.application();
    if (app?.isFiledByProxy) {
      this.proxyPersonForm.patchFormValues(app);
    }
    return app?.applicants?.map(applicant => {
      const v: PatchApplicantApplicantItemRequestParams = {
        id: '',
        applicant: {
          application: app?.uuid,
          entityType: applicant.entityType?.id || '',
          firstName: applicant?.firstName || '',
          lastName: applicant?.lastName || '',
          name: applicant?.name || '',
          pesel: applicant?.pesel || '',
          nip: applicant?.nip || '',
          city: applicant.city,
          email: applicant.email,
          apartmentNumber: applicant.apartmentNumber,
          houseNumber: applicant.houseNumber,
          phoneNumber: applicant.phoneNumber,
          phoneNumber2: applicant.phoneNumber2,
          postalCode: applicant.postalCode,
          postOffice: applicant.postOffice,
          street: applicant.street
        }
      };
      return v;
    }) || [];
  });

  private fetchApplicants: Subscription = toObservable(this.applicationApplicants).subscribe(v => {
    this.applicants.set(v);
  });

  public currentIndex: WritableSignal<number | undefined> = signal(undefined);

  public pending: Signal<boolean> = this.store$.selectSignal(selectPending);

  public loading: Signal<boolean> = this.store$.selectSignal(selectLoading);

  @ViewChild('tabViewComponent', { static: false })
  tabViewComponent: TabViewComponent | undefined;

  constructor(
    public applicantForm: ApplicantForm,
    public proxyPersonForm: ProxyPersonForm,
    private router: Router,
    private route: ActivatedRoute,
    private store$: Store
  ) {
  }

  public onAdd(): void {
    this.showItemForm = true;
    this.currentIndex.set(undefined);
  }

  public onCancel(): void {
    this.router.navigate([this.applicationId ? '../../../' : '../../'], { relativeTo: this.route })
      .then(() => this.store$.dispatch(setApplication({ application: null })));
  }

  public submitAndGoNext(): void {
    if (this.applicants().length && this.proxyPersonForm.valid) {
      if (this.applicationId) {
        this.updateApplication();
      } else {
        this.createApplication();
      }
    }
    if (this.proxyPersonForm.invalid) {
      this.proxyPersonForm.markAllAsTouched();
    }
  }

  private createApplication(): void {
    this.store$.dispatch(
      createApplication({
        application: this.proxyPersonForm.toWriteModel(),
        applicants: this.applicants(),
        serviceTypeId: this.serviceType()?.uuid || '',
        successFnc: (applicationId: string) => {
          this.router.navigate(
            ['../', applicationId, 'dane-wniosku'],
            {
              relativeTo: this.route
            }
          );
        },
        failureFnc: () => {
        }
      })
    );
  }

  private updateApplication(): void {
    this.store$.dispatch(updateApplicants({
      applicationId: this.applicationId || '',
      application: this.proxyPersonForm.toWriteModel(),
      applicants: this.applicants(),
      successFnc: (applicationId) => {
        this.router.navigate(
          ['../../', applicationId, 'dane-wniosku'],
          {
            relativeTo: this.route
          }
        );
      },
      failureFnc: () => {
      }
    }));
  }

  public onUpdateApplicants(applicant: ApplicantAppEntityApplicantWrite): void {
    if (this.currentIndex() !== undefined) {
      this.applicants.update(v => {
        v[this.currentIndex() || 0] = { applicant, id: v[this.currentIndex() || 0].id };
        return v;
      });
      this.currentIndex.set(undefined);
    } else {
      this.applicants.update(v => ([...v, { applicant, id: '' }]));
    }
    this.showItemForm = false;
    this.applicantForm.reset();
  }

  public onRemove(i: number, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.applicants.update(v => v.filter((_, index) => index != i));
  }

  public onEditApplicant(event: Event, i: number): void {
    const applicant: ApplicantAppEntityApplicantWrite | undefined = this.applicants().at(i)?.applicant;
    const tabName: string = this.entityTypes()?.filter(v => v.id === applicant?.entityType)[0]?.name || '';

    event.preventDefault();
    event.stopPropagation();

    if (applicant && tabName) {
      this.currentIndex.set(i);
      this.showItemForm = true;

      setTimeout(() => {

        this.tabViewComponent?.onNavigate(this.tabViewComponent?.panels.filter(v => v?.name === tabName)[0]);
        this.tabViewComponent?.ref.detectChanges();

        this.applicantForm.patchValue({
          ...applicant,
          zipCode: applicant?.postalCode,
          buildingNumber: applicant?.houseNumber,
          phone: applicant?.phoneNumber,
          phone2: applicant?.phoneNumber2
        });
      });
    }
  }

  public ngOnInit(): void {
    this.store$.dispatch(loadEntityTypes());
  }

  public ngOnDestroy(): void {
    this.fetchApplicants.unsubscribe();
  }
}
